import {Box, Grid, IconButton, Stack, Typography} from '@mui/material';
import {t} from 'i18next';
import './ViewIpRestriction.scss';
import ArrCheckbox from '../common/arr-checkbox/ArrCheckbox';
import {useEffect, useState} from 'react';
import ArrTextField from '../common/arr-text-field/ArrTextField';
import {
  useGetIpRestriction,
  usePostIpRestrictions,
} from '../../hooks/ipRestriction/useIpRestriction';
import {IPRange} from '../../api/types/ipRestrictionApi.type';
import ArrButton from '../common/arr-button/ArrButton';
import {GridColDef, GridRenderCellParams} from '@mui/x-data-grid';
import ArrDataGrid from '../common/arr-data-grid/ArrDataGrid';
import {ReactComponent as Delete} from '../../assets/images/delete-icon.svg';
import {ReactComponent as InfoIcon} from '../../assets/images/info-icon.svg';
import {ToastType} from '../../shared/constants';
import ArrModal from '../common/arr-modal/ArrModal';
import ArrTooltipWrapper from '../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import ArrToastNotification from '../common/arr-toast-notification/ArrToastNotification';
import {v4 as uuidv4} from 'uuid';
import ipRegex from 'ip-regex';

const ipv4PatternSafe = ipRegex.v4({exact: true});

const ViewIpRestriction = () => {
  // isIPRestriction from API will be toggle to the state of checkbox "applyIPRestriction"
  const [applyIPRestriction, setApplyIPRestriction] = useState(true);
  const [fromRange, setFromRange] = useState('');
  const [toRange, setToRange] = useState('');
  const [ipRange, setIpRange] = useState<(IPRange & {uuid: string})[] | null>(
    null
  );
  const [fromError, setFromError] = useState(false);
  const [toError, setToError] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [duplicateIp, setDuplicateIp] = useState(false);
  const [myIp, setMyIp] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const {data = null, refetch} = useGetIpRestriction();

  const handleReset = () => {
    setApplyIPRestriction(!data?.isIPRestriction);
    setMyIp(data?.clientIpAdddress || '');
    setIpRange(
      data?.ipRange?.map((r) => ({
        ...r,
        uuid: uuidv4(),
      })) ?? null
    );
    setFromRange('');
    setToRange('');
    setFromError(false);
    setToError(false);
    setDuplicateIp(false);
  };

  useEffect(() => {
    handleReset();
  }, [data]);

  // --- IP validation regex (IPv4 only, can extend to IPv6 if needed)
  const isValidIp = (ip: string) => {
    return ipv4PatternSafe.test(ip);
  };

  const handleIpChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    source = ''
  ): void => {
    const value = e.target.value;
    if (source === 'from') {
      setFromRange(value);
      setFromError(value !== '' && !isValidIp(value));
    } else {
      setToRange(value);
      setToError(value !== '' && !isValidIp(value));
    }
  };

  const handleAddIpRange = () => {
    if (!fromRange || !toRange) return;
    const newEntry = {
      ipRangeFrom: fromRange.trim(),
      ipRangeTo: toRange.trim(),
      uuid: uuidv4(),
    };
    const isDuplicate = ipRange?.some(
      (range) =>
        range.ipRangeFrom === newEntry.ipRangeFrom &&
        range.ipRangeTo === newEntry.ipRangeTo
    );
    if (isDuplicate) {
      setDuplicateIp(true);
      return;
    }
    const updatedIpRange = ipRange ? [...ipRange, newEntry] : [newEntry];
    setIpRange(updatedIpRange);
    setFromRange('');
    setToRange('');
    setFromError(false);
    setToError(false);
    setDuplicateIp(false);
  };

  const handleRangeDelete = (uuid: string) => {
    const updatedRange = ipRange?.filter((item) => item.uuid !== uuid) ?? [];
    setIpRange(updatedRange);
    setShowModal(false);
    setDeleteId(null);
  };

  const {mutate: postIpRestrictions, isError} = usePostIpRestrictions();

  const columns: GridColDef[] = [
    {
      field: 'ipRange',
      headerName: t('ipRestriction.ipRange'),
      flex: 1,
      headerClassName: 'typo-text label2_size',
      sortable: false,
    },
    {
      field: '',
      headerName: '',
      width: 50,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => (
        <IconButton
          disabled={applyIPRestriction}
          onClick={() => {
            setDeleteId(params.row.uuid);
            setShowModal(true);
          }}
          aria-label="delete-ip-range"
          data-testid={`delete-ip-${params.row.uuid}`}
          tabIndex={0}
          className="arr-datagrid__action-btn"
        >
          <Delete />
        </IconButton>
      ),
    },
  ];

  const handleOnSave = () => {
    postIpRestrictions(
      {ipRanges: ipRange || [], isIPRestriction: !applyIPRestriction},
      {
        onSuccess: () => {
          refetch();
          setShowToast(true);
        },
        onError: () => {
          setShowToast(true);
        },
      }
    );
  };

  const disableAdd =
    !fromRange ||
    !toRange ||
    fromError ||
    toError ||
    !isValidIp(fromRange) ||
    !isValidIp(toRange) ||
    applyIPRestriction;

  const disableSave = !applyIPRestriction && !ipRange?.length;

  let inputState: 'inactive' | 'error' | 'enabled';

  if (applyIPRestriction) {
    inputState = 'inactive';
  } else if (fromError) {
    inputState = 'error';
  } else {
    inputState = 'enabled';
  }

  return (
    <Box className="ip-restriction" data-testid="ip-restriction">
      <Box className="title" aria-label={t('ipRestriction.title')}>
        {t('ipRestriction.title')}
        <Box paddingLeft={1}>
          <ArrTooltipWrapper
            title={t('ipRestriction.title')}
            trigger={
              <InfoIcon
                className="info-icon"
                fontSize="medium"
                aria-label="information"
                data-testid="ip-tooltip-info"
                tabIndex={0}
              />
            }
          >
            <Box>{t('ipRestriction.toolTip')}</Box>
          </ArrTooltipWrapper>
        </Box>
      </Box>
      <Grid container>
        <Grid size={9}>
          <Stack className="data">
            <ArrCheckbox
              checked={applyIPRestriction}
              onChange={(checked) => setApplyIPRestriction(checked)}
              label={t('ipRestriction.noIpLabel')}
              labelText={t('ipRestriction.noIpLabel')}
              aria-label="toggle-ip-restriction"
              data-testid="toggle-ip-restriction"
              size="small"
            />

            <Box className="form">
              <Typography sx={{paddingBottom: 1}} data-testid={`view-ip-restriction-from`}>
                {t('ipRestriction.from')}: *
              </Typography>
              <ArrTextField
                className={fromError ? 'error-position' : ''}
                sizeVariant="medium"
                state={inputState}
                placeholder={t('ipRestriction.from')}
                aria-label="ip-from"
                dataTestId="ip-from"
                tabIndex={0}
                value={fromRange}
                onChange={(e) => handleIpChange(e, 'from')}
                error={fromError}
                supportText={fromError ? t('ipRestriction.invalidIp') : ''}
              />
              <Typography sx={{paddingBottom: 1}} data-testid={`view-ip-restriction-to`}>
                {t('ipRestriction.to')}: *
              </Typography>
              <ArrTextField
                state={inputState}
                className={toError ? 'error-position' : ''}
                sizeVariant="medium"
                placeholder={t('ipRestriction.to')}
                aria-label="ip-to"
                dataTestId="ip-to"
                tabIndex={0}
                value={toRange}
                onChange={(e) => handleIpChange(e, 'to')}
                error={toError}
                supportText={toError ? t('ipRestriction.invalidIp') : ''}
              />
              <ArrButton
                size="medium"
                onClick={handleAddIpRange}
                disabled={disableAdd}
                aria-label="add-ip-range"
                dataTestId="add-ip-range"
                tabIndex={0}
              >
                {t('ipRestriction.add')}
              </ArrButton>
            </Box>

            <Box>
              <ArrDataGrid
                columns={columns}
                data-testid="ip-range-grid"
                aria-label="ip-range-grid"
                rows={
                  ipRange?.map((item) => ({
                    ipRange: `${item.ipRangeFrom} To ${item.ipRangeTo}`,
                    id: item.uuid,
                    uuid: item.uuid,
                  })) ?? []
                }
                noRowsOverlayCustomText={t('ipRestriction.noIpRange')}
              />
            </Box>

            <Box display="flex" justifyContent="space-between">
              <Box className="yourIp" data-testid="your-ip">
                <Typography data-testid={`view-ip-restriction-your-ip`}>
                  {t('ipRestriction.yourIp')}: {myIp}
                </Typography>
              </Box>
              <Box>
                <ArrButton
                  variant="secondary"
                  onClick={handleReset}
                  size="medium"
                  aria-label="cancel"
                  dataTestId="view-ip-restriction-cancel-button"
                  tabIndex={0}
                >
                  {t('ipRestriction.cancel')}
                </ArrButton>
                <ArrButton
                  size="medium"
                  sx={{marginLeft: 2}}
                  disabled={disableSave}
                  onClick={handleOnSave}
                  aria-label="save"
                  dataTestId="save-button"
                  tabIndex={0}
                >
                  {t('ipRestriction.save')}
                </ArrButton>
              </Box>
            </Box>
          </Stack>
        </Grid>
        <Grid size={3}></Grid>
      </Grid>
      {showModal && deleteId && (
        <ArrModal
          showModal={showModal}
          onClose={() => setShowModal(false)}
          title={t('ipRestriction.delete')}
          aria-label="delete-ip-modal"
          dataTestId="arr-ipRestriction-delete-modal"
          buttons={[
            {
              id: t('ipRestriction.no'),
              label: t('ipRestriction.no'),
              onClick: () => setShowModal(false),
              variant: 'secondary',
              dataTestId: 'aird-modal-no',
            },
            {
              id: t('ipRestriction.yes'),
              label: t('ipRestriction.yes'),
              onClick: () => handleRangeDelete(deleteId),
              variant: 'primary',
              dataTestId: 'aird-modal-yes',
            },
          ]}
        >
          <Typography color="white" data-testid={`view-ip-restriction-modal-desc-text`}>
            {t('ipRestriction.modalDescText')}
          </Typography>
        </ArrModal>
      )}
      {duplicateIp ? (
        <ArrToastNotification
          message={
            isError
              ? t('ipRestriction.toastSucces')
              : t('ipRestriction.duplicateIp')
          }
          onClose={() => setDuplicateIp(false)}
          status={[isError ? ToastType.Success : ToastType.Error]}
          data-testid="duplicate-ip-error"
        />
      ) : (
        showToast && (
          <ArrToastNotification
            message={
              isError
                ? t('ipRestriction.toastFail')
                : t('ipRestriction.toastSucces')
            }
            onClose={() => setShowToast(false)}
            status={[isError ? ToastType.Error : ToastType.Success]}
            data-testid="toast-message"
          />
        )
      )}
    </Box>
  );
};

export default ViewIpRestriction;
